import { useCallback } from 'react';

import { TTemplateMediaFieldName, UploadControlModeEnum } from '../types/templates';
import { useTemplateMediaUpload } from './useTemplateImageUpload';
import { useTemplateOverrideMode } from './useTemplateOverrideMode';

interface IUseTemplateUploadHandlersProps {
  mode: UploadControlModeEnum;
  overrideValue: string | undefined;
  fieldName: TTemplateMediaFieldName;
  onDrop?: (files: File[]) => void;
  onUpload?: (url: string) => void;
  onRemove: () => void;
  onClearOverride?: () => void;
}

export const useTemplateUploadHandlers = ({
  mode,
  overrideValue,
  fieldName,
  onDrop: onDropProp,
  onUpload,
  onRemove,
  onClearOverride,
}: IUseTemplateUploadHandlersProps) => {
  // Use common override mode detection hook
  const { isPageOverrideMode, isOverridden, shouldClearOverride } = useTemplateOverrideMode({
    mode,
    overrideValue,
    onClearOverride,
  });

  const { handleMediaUpload } = useTemplateMediaUpload({
    onSuccess: (_, url) => {
      if (url && onUpload) {
        onUpload(url);
      }
    },
  });

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (onDropProp) {
        onDropProp(acceptedFiles);
      } else {
        handleMediaUpload(fieldName)(acceptedFiles);
      }
    },
    [onDropProp, handleMediaUpload, fieldName],
  );

  const handleRemoveClick = useCallback(() => {
    if (shouldClearOverride) {
      onClearOverride!();
    } else {
      onRemove();
    }
  }, [shouldClearOverride, onClearOverride, onRemove]);

  return {
    isPageOverrideMode,
    isOverridden,
    shouldClearOverride,
    handleDrop,
    handleRemoveClick,
  };
};
